import React, { createContext, useContext, useState, useEffect } from "react";
import { Product, ProductPrice } from "../types";
import { basketService } from "../services/api";
import { useAuth } from "./AuthContext";

export interface CartItem {
    product: Product;
    selectedPrice: ProductPrice | null;
    price: number;
    quantity: number;
    label: string;
}

interface CartContextType {
    items: CartItem[];
    addToCart: (product: Product, selectedPrice: ProductPrice | null) => void;
    removeFromCart: (index: number) => void;
    clearCart: () => void;
    totalPrice: number;
    isOpen: boolean;
    setIsOpen: (v: boolean) => void;
}

const CartContext = createContext<CartContextType | null>(null);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const { user, isAuthenticated } = useAuth();

    // Charger le panier depuis le backend au login (clients uniquement)
    useEffect(() => {
        if (!isAuthenticated || !user || user.role !== "client") {
            setItems([]);
            return;
        }
        basketService.getByUser(user.id).catch(() => {});
    }, [isAuthenticated, user]);

    const addToCart = (product: Product, selectedPrice: ProductPrice | null) => {
        const price = selectedPrice?.price ?? product.price;
        const quantity = selectedPrice?.quantity ?? 1;
        const label = selectedPrice?.label || `${quantity} ${selectedPrice?.price_unit ?? product.price_unit}`;
        setItems((prev) => [...prev, { product, selectedPrice, price, quantity, label }]);
        setIsOpen(true);

        // Persister en base (clients uniquement)
        if (user && user.role === "client") {
            basketService.add({
                user_id: user.id,
                product_id: product.id,
                quantity,
                price,
            }).catch(() => {});
        }
    };

    const removeFromCart = (index: number) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const clearCart = () => setItems([]);

    const totalPrice = items.reduce((sum, item) => sum + item.price, 0);

    return (
        <CartContext.Provider value={{ items, addToCart, removeFromCart, clearCart, totalPrice, isOpen, setIsOpen }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
